import { ArrowRight, MoreHorizontal, Copy } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ServicePackage } from '../types';
import {
  PACKAGE_TYPE_LABELS,
  latestRevisionForTerm,
  formatCents,
  uniqueTerms,
} from '../types';
import i18n from '@/utils/i18n';

interface ServicePackageCardProps {
  pkg: ServicePackage;
  onManage: () => void;
  onClone: () => void;
}

export function ServicePackageCard({
  pkg,
  onManage,
  onClone,
}: ServicePackageCardProps) {
  const terms = uniqueTerms(pkg.revisions);

  return (
    <Card className="tw:flex tw:flex-col tw:min-w-[240px] tw:flex-1">
      <CardHeader className="tw:pb-2">
        <div className="tw:flex tw:items-start tw:justify-between tw:gap-2">
          <CardTitle className="tw:text-xl">
            {PACKAGE_TYPE_LABELS[pkg.package_type]}
          </CardTitle>
          <div className="tw:flex tw:items-center tw:gap-1">
            <Badge variant="outline">{pkg.currency}</Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" className="tw:-mr-1">
                  <MoreHorizontal className="tw:size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {/* onSelect prevent-default stops Radix from firing a pointerdown-outside
                    event that would immediately dismiss the Dialog before it opens */}
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    onClone();
                  }}
                >
                  <Copy className="tw:size-4" />
                  {i18n.t('ServicePackage:copyToCurrency')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>

      <CardContent className="tw:flex tw:flex-col tw:gap-3 tw:flex-1">
        {terms.length > 0 ? (
          <dl className="tw:flex tw:flex-col tw:gap-1.5 tw:text-sm">
            {terms.map((term) => {
              const latest = latestRevisionForTerm(pkg.revisions, term)!;
              return (
                <div
                  key={term}
                  className="tw:flex tw:items-center tw:justify-between tw:gap-4"
                >
                  <dt className="tw:text-muted-foreground">
                    {i18n.t('ServicePackage:nMonths', { n: term })}
                  </dt>
                  <dd className="tw:font-medium">
                    {formatCents(
                      latest.monthly_service_fee_in_cents,
                      pkg.currency,
                    )}
                    {i18n.t('ServicePackage:perMonth')}
                  </dd>
                </div>
              );
            })}
          </dl>
        ) : (
          <p className="tw:text-sm tw:text-muted-foreground">
            {i18n.t('ServicePackage:noTermsConfigured')}
          </p>
        )}

        <div className="tw:mt-auto tw:pt-2">
          <Button variant="outline" className="tw:w-full" onClick={onManage}>
            {i18n.t('ServicePackage:manage')}
            <ArrowRight className="tw:size-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
